import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Typography,
    CircularProgress,
    Box,
    IconButton,
    Alert,
    ThemeProvider,
    createTheme,
    Autocomplete,
    Divider,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import 'dayjs/locale/he';
import { prefixer } from 'stylis';
import rtlPlugin from 'stylis-plugin-rtl';
import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';
import CloseIcon from '@mui/icons-material/Close';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import SearchIcon from '@mui/icons-material/Search';
import axios from 'axios';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '../../../authConfig';
import styles from '../../../styles/purchaseInvoices/invoiceManagement/CreateGoodsReceiptDialogStyles';

const cacheRtl = createCache({
    key: 'muirtl',
    stylisPlugins: [prefixer, rtlPlugin],
});

const rtlTheme = createTheme({
    direction: 'rtl',
});


const CreateGoodsReceiptDialog = ({
    open,
    onClose,
    onSubmit,
    invoice
}) => {
    const { instance, accounts } = useMsal();
    const [orderNumber, setOrderNumber] = useState('');
    const [receiptDate, setReceiptDate] = useState(dayjs());
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [openOrders, setOpenOrders] = useState([]);
    const [isLoadingOrders, setIsLoadingOrders] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState(null);

    useEffect(() => {
        const fetchOpenOrders = async () => {
            const supplierNum = invoice?.supNumber;
            
            console.log('CreateGoodsReceiptDialog - invoice fields:', {
                open,
                supNumber: invoice?.supNumber,
                supName: invoice?.supName,
                companyId: invoice?.companyId,
                allInvoiceKeys: invoice ? Object.keys(invoice) : []
            });
            
            if (!open || !supplierNum) {
                console.log('Skipping fetch - open:', open, 'supplierNum:', supplierNum);
                return;
            }
            
            setIsLoadingOrders(true);
            try {
                const tokenResponse = await instance.acquireTokenSilent({
                    ...loginRequest,
                    account: accounts[0],
                });

                console.log('Fetching open orders for supplier:', supplierNum);
                
                const response = await axios.get(
                    `${process.env.REACT_APP_BACKEND_BASE_URL}/api/goods-receipt/open-orders/${supplierNum}`,
                    { headers: { Authorization: `Bearer ${tokenResponse.accessToken}` } }
                );

                console.log('Open orders response:', response.data);
                setOpenOrders(response.data.data || []);
            } catch (err) {
                console.error('Error fetching open orders:', err);
            } finally {
                setIsLoadingOrders(false);
            }
        };

        fetchOpenOrders();
    }, [open, invoice?.supNumber, instance, accounts]);

    const handleClose = () => {
        if (!isSubmitting) {
            setOrderNumber('');
            setReceiptDate(dayjs());
            setError('');
            setSelectedOrder(null);
            setOpenOrders([]);
            onClose();
        }
    };

    const handleOrderSelect = (event, newValue) => {
        setSelectedOrder(newValue);
        if (newValue) {
            setOrderNumber(newValue.orderName);
        }
    };

    const handleSubmit = async () => {
        if (!orderNumber.trim()) {
            setError('יש להזין מספר הזמנה');
            return;
        }

        if (!receiptDate || !receiptDate.isValid()) {
            setError('יש לבחור תאריך קבלה תקין');
            return;
        }

        setError('');
        setIsSubmitting(true);

        try {
            await onSubmit({
                orderNumber: orderNumber.trim(),
                receiptDate: receiptDate.format('YYYY-MM-DD'),
                invoiceId: invoice?.id
            });
        } catch (err) {
            setError(err.message || 'שגיאה בשליפת נתוני ההזמנה');
        } finally {
            setIsSubmitting(false);
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        return date.toLocaleDateString('he-IL');
    };

    const formatCurrency = (value) => {
        if (!value) return '₪0';
        return new Intl.NumberFormat('he-IL', {
            style: 'currency',
            currency: 'ILS',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(value);
    };

    return (
        <CacheProvider value={cacheRtl}>
            <ThemeProvider theme={rtlTheme}>
                <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="he">
                    <Dialog
                        open={open}
                        onClose={handleClose}
                        sx={styles.dialog}
                        dir="rtl"
                        maxWidth="sm"
                        fullWidth
                    >
                        <DialogTitle sx={styles.dialogTitle}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <ReceiptLongIcon color="primary" />
                                <Typography sx={styles.titleText}>
                                    יצירת קבלת סחורה
                                </Typography>
                            </Box>
                            <IconButton
                                onClick={handleClose}
                                sx={styles.closeButton}
                                disabled={isSubmitting}
                            >
                                <CloseIcon />
                            </IconButton>
                        </DialogTitle>

                        <DialogContent sx={styles.dialogContent}>
                            {invoice && (
                                <Alert severity="info" sx={styles.infoAlert}>
                                    יצירת קבלת סחורה עבור חשבונית: <strong>{invoice.booknum || invoice.fileName}</strong>
                                    {(invoice.supName || invoice.supplierName) && ` | ספק: ${invoice.supName || invoice.supplierName}`}
                                </Alert>
                            )}

                            <Box sx={{ mt: 2 }}>
                                <Typography sx={styles.fieldLabel}>
                                    בחר הזמנה מהרשימה
                                </Typography>
                                <Autocomplete
                                    options={openOrders}
                                    value={selectedOrder}
                                    onChange={handleOrderSelect}
                                    getOptionLabel={(option) => option.orderName || ''}
                                    loading={isLoadingOrders}
                                    disabled={isSubmitting}
                                    loadingText={
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 2, justifyContent: 'center' }}>
                                            <CircularProgress size={20} />
                                            <Typography sx={{ color: '#64748b' }}>טוען הזמנות פתוחות...</Typography>
                                        </Box>
                                    }
                                    noOptionsText={
                                        isLoadingOrders ? 'טוען...' : 'לא נמצאו הזמנות פתוחות ללא קבלת סחורה'
                                    }
                                    ListboxProps={{
                                        sx: {
                                            maxHeight: 350,
                                            '& .MuiAutocomplete-option': {
                                                borderBottom: '1px solid #f1f5f9',
                                                '&:last-child': { borderBottom: 'none' }
                                            }
                                        }
                                    }}
                                    renderOption={(props, option) => {
                                        const { key, ...otherProps } = props;
                                        return (
                                            <Box 
                                                component="li" 
                                                key={key}
                                                {...otherProps} 
                                                sx={{ 
                                                    py: 1.5,
                                                    px: 2,
                                                    '&:hover': { bgcolor: '#f8fafc' }
                                                }}
                                            >
                                                {/* Main row - Order number and price */}
                                                <Box sx={{ 
                                                    display: 'flex', 
                                                    justifyContent: 'space-between', 
                                                    alignItems: 'center',
                                                    mb: 0.5
                                                }}>
                                                    <Typography sx={{ 
                                                        fontWeight: 700, 
                                                        color: '#1e40af',
                                                        fontSize: '0.95rem',
                                                        fontFamily: 'monospace',
                                                        letterSpacing: '0.5px'
                                                    }}>
                                                        {option.orderName}
                                                    </Typography>
                                                    <Box sx={{ 
                                                        bgcolor: '#dbeafe', 
                                                        color: '#1e40af',
                                                        px: 1.5,
                                                        py: 0.5,
                                                        borderRadius: 1,
                                                        fontWeight: 600,
                                                        fontSize: '0.85rem'
                                                    }}>
                                                        {formatCurrency(option.totalPrice)}
                                                    </Box>
                                                </Box>
                                                {/* Second row - Date and details */}
                                                <Box sx={{ 
                                                    display: 'flex', 
                                                    alignItems: 'center',
                                                    gap: 1.5
                                                }}>
                                                    <Typography sx={{ 
                                                        fontSize: '0.8rem', 
                                                        color: '#64748b',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 0.5
                                                    }}>
                                                        📅 {formatDate(option.date)}
                                                    </Typography>
                                                    {option.details && (
                                                        <Typography sx={{ 
                                                            fontSize: '0.8rem', 
                                                            color: '#94a3b8',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap',
                                                            maxWidth: '200px'
                                                        }}>
                                                            • {option.details}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </Box>
                                        );
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            placeholder={isLoadingOrders ? "טוען הזמנות..." : "בחר הזמנה..."}
                                            sx={styles.formField}
                                            InputProps={{
                                                ...params.InputProps,
                                                startAdornment: (
                                                    <>
                                                        {isLoadingOrders ? (
                                                            <CircularProgress size={18} sx={{ mr: 1 }} />
                                                        ) : (
                                                            <SearchIcon sx={{ color: '#94a3b8', mr: 1 }} />
                                                        )}
                                                    </>
                                                ),
                                                endAdornment: params.InputProps.endAdornment,
                                            }}
                                        />
                                    )}
                                />
                                {!isLoadingOrders && openOrders.length > 0 && (
                                    <Typography sx={{ fontSize: '0.75rem', color: '#94a3b8', mt: 0.5, textAlign: 'center' }}>
                                        {openOrders.length} הזמנות פתוחות ללא קבלת סחורה
                                    </Typography>
                                )}
                                <Divider sx={{ my: 2 }}>
                                    <Typography sx={{ color: '#94a3b8', fontSize: '0.75rem' }}>
                                        או הקלד ידנית
                                    </Typography>
                                </Divider>
                            </Box>

                            <Box sx={{ mt: 0 }}>
                                <Typography sx={styles.fieldLabel}>
                                    מספר הזמנה *
                                </Typography>
                                <TextField
                                    fullWidth
                                    value={orderNumber}
                                    onChange={(e) => {
                                        setOrderNumber(e.target.value);
                                        setSelectedOrder(null); // Clear selection when typing manually
                                    }}
                                    placeholder="הזן מספר הזמנה"
                                    sx={styles.formField}
                                    disabled={isSubmitting}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' && !isSubmitting) {
                                            handleSubmit();
                                        }
                                    }}
                                />
                            </Box>

                            <Box>
                                <Typography sx={styles.fieldLabel}>
                                    תאריך קבלה *
                                </Typography>
                                <DatePicker
                                    value={receiptDate}
                                    onChange={(newDate) => setReceiptDate(newDate)}
                                    format="DD/MM/YYYY"
                                    disabled={isSubmitting}
                                    slotProps={{
                                        textField: {
                                            fullWidth: true,
                                            sx: styles.formField
                                        }
                                    }}
                                />
                            </Box>

                            {error && (
                                <Alert severity="error" sx={styles.errorAlert}>
                                    {error}
                                </Alert>
                            )}
                        </DialogContent>

                        <DialogActions sx={styles.dialogActions}>
                            <Button
                                variant="outlined"
                                onClick={handleClose}
                                disabled={isSubmitting}
                                sx={styles.cancelButton}
                            >
                                ביטול
                            </Button>
                            <Button
                                variant="contained"
                                onClick={handleSubmit}
                                disabled={isSubmitting || !orderNumber.trim()}
                                sx={styles.submitButton}
                            >
                                {isSubmitting ? (
                                    <Box sx={styles.loadingBox}>
                                        <CircularProgress size={18} color="inherit" />
                                        <span>טוען נתוני הזמנה...</span>
                                    </Box>
                                ) : (
                                    'המשך'
                                )}
                            </Button>
                        </DialogActions>
                    </Dialog>
                </LocalizationProvider>
            </ThemeProvider>
        </CacheProvider>
    );
};

export default CreateGoodsReceiptDialog;
